import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { landTypes, otherTypes, processSteps, workingPrinciples } from '../../../data/homeContent';

export const SectionHeading = ({ kicker, title, text, align = 'left', dark = false, children }) => (
  <div className={`hv-section-head ${align === 'center' ? 'text-center mx-auto' : ''} max-w-[760px] mb-14`}>
    {kicker && <p className={`hv-kicker text-xs font-semibold uppercase tracking-[0.18em] mb-5 ${dark ? 'text-[#DFC590]' : 'text-[#647064]'}`}>{kicker}</p>}
    <h2 className={dark ? 'text-[#F8F6F0]' : 'text-[#19392B]'}>{title}</h2>
    {text && <p className={`mt-6 text-lg leading-relaxed ${dark ? 'text-[#C9D3C8]' : 'text-[#647064]'}`}>{text}</p>}
    {children}
  </div>
);

export const TrustSection = () => (
  <section className="hv-trust bg-[#F8F6F0] py-24 lg:py-32" data-testid="homepage-trust">
    <div className="container mx-auto px-4">
      <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.18em] text-[#647064] mb-8">So arbeiten wir</p>
      <h2 className="hv-statement text-[#19392B] max-w-[22ch] mb-16 lg:mb-24">
        Wir verkaufen Acker, Wald, Grünland und Bauernhöfe. <em className="hv-accent-green">Sonst nichts.</em>
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12" data-testid="working-principles">
        {workingPrinciples.map((p, i) => (
          <div key={p.title} className="hv-principle border-t border-[#19392B]/30 pt-6">
            <span className="hv-serif text-4xl text-[#B89B5E]">0{i + 1}</span>
            <h3 className="text-xl text-[#19392B] mt-5 mb-3">{p.title}</h3>
            <p className="text-[#647064] leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>
      <Link to="/warum-agrarkapital" data-testid="trust-more-link" className="hv-link inline-flex items-center gap-2 mt-16 text-[#19392B] font-medium underline underline-offset-8 decoration-[#DFC590]">
        Mehr über AgrarKapital <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </div>
  </section>
);

export const LandTypesSection = () => (
  <section className="hv-landtypes bg-[#EEE9DC] py-24 lg:py-32" data-testid="homepage-property-types">
    <div className="container mx-auto px-4">
      <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
        <SectionHeading kicker="Unsere Schwerpunkte" title="Jede Fläche hat ihre eigene Preislogik." />
        <p className="text-lg text-[#647064] max-w-[40ch] mb-14 lg:text-right">Deshalb bewerten wir Acker, Wald, Grünland und Hofstellen getrennt – mit den Kriterien, die Käufer in Ihrer Region wirklich zahlen.</p>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {landTypes.map((t, i) => (
          <Link key={t.key} to={t.to} data-testid={`property-type-card-${i}`} className="hv-tile group relative block aspect-[3/4] overflow-hidden rounded-[4px] bg-[#153B2B]">
            <img src={t.image} alt={t.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
            <div className="hv-tile-fade absolute inset-0" />
            <div className="absolute inset-x-0 bottom-0 p-7 lg:p-8 text-[#F8F6F0]">
              <t.icon className="w-6 h-6 text-[#DFC590] mb-4" aria-hidden="true" />
              <h3 className="hv-serif text-[30px] leading-[1.1] mb-3">{t.title}</h3>
              <p className="text-[#DDE4DC] leading-relaxed mb-4 max-w-[34ch]">{t.text}</p>
              <ul className="hv-tile-points space-y-1.5 mb-5">
                {t.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2 text-sm text-[#DDE4DC]"><Check className="w-4 h-4 mt-0.5 text-[#DFC590] shrink-0" aria-hidden="true" />{pt}</li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-2 text-[#DFC590] font-medium">Preisfaktoren ansehen <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-14" data-testid="other-property-types">
        <p className="hv-kicker text-xs font-semibold uppercase tracking-[0.18em] text-[#647064] mb-5">Außerdem vermitteln und bewerten wir</p>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
          {otherTypes.map((o, i) => (
            <Link key={o.to} to={o.to} data-testid={`other-type-card-${i}`} className="hv-card hv-card-light group bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] p-5 lg:p-6 flex flex-col">
              <o.icon className="w-6 h-6 text-[#1C432F] mb-4" aria-hidden="true" />
              <span className="text-[17px] font-medium text-[#19392B] leading-snug">{o.label}</span>
              <span className="text-sm text-[#647064] mt-1.5 mb-4">{o.text}</span>
              <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-[#19392B]">Mehr erfahren <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const ProcessSection = () => (
  <section id="process-section" className="hv-process bg-[#FFFEFA] py-24 lg:py-32" data-testid="homepage-process">
    <div className="container mx-auto px-4">
      <SectionHeading kicker="Der Ablauf" title="Vier Schritte. Sie entscheiden nach jedem einzelnen, ob es weitergeht." />
      <ol className="grid md:grid-cols-2 xl:grid-cols-4 gap-x-10 gap-y-12">
        {processSteps.map((s, i) => (
          <li key={s.title} className="hv-step border-t border-[#19392B]/30 pt-6" data-testid={`process-step-${i + 1}`}>
            <div className="flex items-center justify-between mb-6">
              <span className="hv-serif text-5xl text-[#B89B5E]">{i + 1}</span>
              <s.icon className="w-6 h-6 text-[#1C432F]" aria-hidden="true" />
            </div>
            <h3 className="text-xl text-[#19392B] mb-3">{s.title}</h3>
            <p className="text-[#647064] leading-relaxed">{s.text}</p>
          </li>
        ))}
      </ol>
      <div className="mt-16 flex flex-wrap items-center gap-6">
        <Link to="/bewertung" data-testid="process-cta" className="hv-btn-primary inline-flex items-center justify-center h-[56px] px-8 rounded-[4px] bg-[#1C432F] hover:bg-[#153B2B] text-[#F8F6F0] font-semibold">Schritt 1 jetzt starten</Link>
        <Link to="/verkaufsprozess" className="hv-link text-[#19392B] font-medium underline underline-offset-8 decoration-[#DFC590]">Den ganzen Verkaufsprozess lesen</Link>
      </div>
    </div>
  </section>
);
